import { useState, useEffect, useContext, useMemo } from 'react';
import { ClientContext } from '../../../../contexts/clientProvider';
import { SecretariasContext } from '../../../../contexts/secretariasProvider';
import { FaturadorContext } from '../../../../contexts/faturadorProvider';
import DataTable from '../../../Table';
import BaseSelect from '../../../General/FormsComp/Select/BaseSelect';
import LoadingSpinner from '../../../Loader/load';

const Secretarias = () => {
  const { clientes, setIdCliente, idCliente } = useContext(ClientContext);
  const { secretarias: allSecretarias } = useContext(SecretariasContext);
  const { patrimonios, loading, toBrl, clienteSelecionado, setClienteSelecionado, calculateTotalByGrupoValor } = useContext(FaturadorContext);

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setClients(clientes ? clientes : []);
  }, [clientes]);

  // Inicializar selectedClient se clienteSelecionado já existe
  useEffect(() => {
    if (clienteSelecionado && !selectedClient && clientes) {
      const cliente = clientes.find(c => c.nome === clienteSelecionado);
      if (cliente) {
        setSelectedClient(String(cliente.id));
      }
    }
  }, [clienteSelecionado, selectedClient, clientes]);

  const handleSelectClient = (e) => {
    const id = e.target.value;
    setSelectedClient(id);
    const selected = clients.find((c) => c.id === parseInt(id));
    if (selected) {
      setIdCliente(id);
      setClienteSelecionado(selected.nome);
    } else {
      setClienteSelecionado('');
    }
  };

  const secretariaNameById = useMemo(() => {
    const map = new Map();
    if (!allSecretarias || allSecretarias.length === 0) return map;
    const currentClientId = String(selectedClient || (clientes?.find(c => c?.nome === clienteSelecionado)?.id ?? ''));
    const list = (allSecretarias || [])
      .filter(s => String(s?.id_cliente || s?.cliente_id) === currentClientId);
    list.forEach(s => map.set(String(s?.id), s?.nome));
    return map;
  }, [allSecretarias, clientes, selectedClient, clienteSelecionado]);

  useEffect(() => {
    if (!patrimonios || patrimonios.length === 0) {
      setRows([]);
      return;
    }

    // Filtrar patrimonios para excluir anexo "ESTOQUE"
    const patrimoniosFiltered = patrimonios.filter(pat => 
      pat.nome_do_anexo !== "ESTOQUE" && pat.nome_do_anexo !== "Estoque" && pat.nome_do_anexo !== "estoque"
    );

    // Agrupar por secretaria_id ou id_secretaria
    const groupsMap = new Map();
    
    for (const pat of patrimoniosFiltered) {
      const secId = String(pat?.secretaria_id ?? pat?.id_secretaria ?? '');
      
      if (!secId || secId === '') continue;
      
      if (!groupsMap.has(secId)) {
        groupsMap.set(secId, []);
      }
      groupsMap.get(secId).push(pat);
    }

    // Calcular totais por secretaria usando a função do provider
    const groups = new Map();
    for (const [secId, pats] of groupsMap.entries()) {
      const total = calculateTotalByGrupoValor(pats);
      groups.set(secId, { quantidade: pats.length, total });
    }

    // Montar linhas da tabela
    let counter = 1;
    const result = Array.from(groups.entries()).map(([secId, { quantidade, total }]) => ({
      id: counter++,
      secretaria: secretariaNameById.get(String(secId)) || `Secretaria ${secId}`,
      quantidade,
      valor_total: total, // manter numérico para formatter do DataTable
    })).sort((a, b) => a.secretaria.localeCompare(b.secretaria));

    setRows(result);
  }, [patrimonios, secretariaNameById]);

  const headers = [
    { label: 'Secretaria', key: 'secretaria', sortable: true, wrapText: true, width: 'w-64' },
    { label: 'Quantidade', key: 'quantidade', sortable: true, width: 'w-32' },
    { label: 'Valor Total', key: 'valor_total', sortable: true, width: 'w-40', type: 'money' },
  ];

  return (
    <>
      <div className="mb-6">
        <BaseSelect
          label="Cliente"
          name="cliente"
          value={selectedClient}
          onChange={handleSelectClient}
          options={clients ? clients.slice().sort((a, b) => a.nome.localeCompare(b.nome)) : []}
          placeholder="Selecione o cliente"
          valueKey="id"
          labelKey="nome"
        />
      </div>



      {loading && <LoadingSpinner />}

      {!loading && clienteSelecionado && rows.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">
              🏢
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Nenhuma secretaria encontrada
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Este cliente não possui patrimônios vinculados a secretarias
            </p>
          </div>
        </div>
      )}

      {rows && rows.length > 0 && (
        <DataTable
          headers={headers}
          data={rows}
          itemsPerPage={5}
          exportButton={true}
          docTitle={`Secretarias do cliente ${clienteSelecionado || ''}`}
        />
      )}
    </>
  );
};

export default Secretarias;


